import { memo } from 'react';
import type { Item } from '../db/types';
import type { Catalog, Section } from '../lib/views';
import { NoteRow } from './NoteRow';
import { TaskRow } from './TaskRow';
import './ListPane.css';

/**
 * One row, and the reason it is its own component: the handlers a row needs are
 * bound to the item it shows, and building them inline made every row a new
 * element on every render. Typing in a note re-renders this pane on each
 * keystroke, so a list of a few thousand rows was rebuilt from scratch for each
 * character. Bound here instead, behind a memo, so only the row that actually
 * changed does any work.
 */
const PaneRow = memo(function PaneRow({
  item,
  catalog,
  selected,
  images,
  dateStyle,
  onOpen,
  onToggle,
  onDelete,
}: {
  item: Item;
  catalog: Catalog;
  selected: boolean;
  images: number;
  dateStyle: 'time' | 'full';
  onOpen: (item: Item) => void;
  onToggle: (item: Item) => void;
  onDelete: (item: Item) => void;
}) {
  return item.kind === 'note' ? (
    <NoteRow
      item={item}
      selected={selected}
      images={images}
      onOpen={() => onOpen(item)}
      onDelete={() => onDelete(item)}
    />
  ) : (
    <TaskRow
      item={item}
      catalog={catalog}
      selected={selected}
      dateStyle={dateStyle}
      onOpen={() => onOpen(item)}
      onToggle={() => onToggle(item)}
      onDelete={() => onDelete(item)}
    />
  );
});

interface Props {
  title: string;
  detail?: string;
  sections: Section[];
  catalog: Catalog;
  imageCounts: Record<string, number>;
  /** Chips for any filters stacked on top of the selection. */
  filters?: React.ReactNode;
  selectedId: string | null;
  /** Sections in date-grouped views own the day, so rows show only a time. */
  dateStyle?: 'time' | 'full';
  emptyTitle: string;
  emptyHint: string;
  quickAdd?: React.ReactNode;
  onOpen: (item: Item) => void;
  onToggle: (item: Item) => void;
  onDelete: (item: Item) => void;
}

export function ListPane({
  title,
  detail,
  sections,
  catalog,
  imageCounts,
  filters,
  selectedId,
  dateStyle = 'full',
  emptyTitle,
  emptyHint,
  quickAdd,
  onOpen,
  onToggle,
  onDelete,
}: Props) {
  return (
    <div className="pane">
      <header className="pane__head">
        <div className="pane__heading">
          <h1 className="pane__title">{title}</h1>
          {detail && <span className="pane__detail">{detail}</span>}
        </div>
        {filters}
        {quickAdd}
      </header>

      <div className="pane__scroll" role="listbox" aria-label={title}>
        {sections.length === 0 ? (
          <div className="empty">
            <div className="empty__title">{emptyTitle}</div>
            <div className="empty__hint">{emptyHint}</div>
          </div>
        ) : (
          sections.map((section) => (
            <div className="pane__section" key={section.key}>
              <div
                className={`section-head${section.tone === 'overdue' ? ' section-head--overdue' : ''}`}
              >
                <span className="pane__sectionLabel">
                  {section.label}
                  {section.sub && <span className="section-head__sub"> {section.sub}</span>}
                </span>
                <span className="section-head__count">{section.items.length}</span>
              </div>

              {section.items.map((item) => (
                <PaneRow
                  key={item.id}
                  item={item}
                  catalog={catalog}
                  selected={item.id === selectedId}
                  images={imageCounts[item.id] ?? 0}
                  dateStyle={dateStyle}
                  onOpen={onOpen}
                  onToggle={onToggle}
                  onDelete={onDelete}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
